import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Plus, Edit, Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import RSVPViewer from './RSVPViewer';

const EMPTY_EVENT = {
  title: '',
  event_date: '',
  event_time: '',
  location: '',
  description: '',
  poster_url: '',
  poster_alt_text: '',
  rsvp_enabled: true,
  status: 'Draft',
};

function StatusPill({ status }) {
  const cls = status === 'Published' ? 'bg-green-100 text-green-700'
    : status === 'Draft' ? 'bg-yellow-100 text-yellow-800'
    : status === 'Archived' ? 'bg-red-50 text-red-700 border border-red-200'
    : 'bg-gray-100 text-gray-600';
  return <span className={`text-xs font-bold px-2 py-1 rounded-full ${cls}`}>{status}</span>;
}

function EventForm({ form, setForm, editing, onSubmit, isSubmitting }) {
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, poster_url: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <form onSubmit={onSubmit} className="card-moj p-5 space-y-3" data-testid="event-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="label-moj">Title *</label>
          <input className="input-moj" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></div>
        <div><label className="label-moj">Status</label>
          <select className="input-moj" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Draft</option><option>Published</option><option>Archived</option>
          </select></div>
        <div><label className="label-moj">Date *</label>
          <input type="date" required className="input-moj" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}/></div>
        <div><label className="label-moj">Time *</label>
          <input className="input-moj" placeholder="10:30" required value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}/></div>
        <div className="sm:col-span-2"><label className="label-moj">Location *</label>
          <input required className="input-moj" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}/></div>
        <div className="sm:col-span-2"><label className="label-moj">Description</label>
          <textarea rows={3} className="input-moj" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/></div>
        <div className="sm:col-span-2"><label className="label-moj">Poster (4:3 recommended, max 5MB)</label>
          <input type="file" accept="image/*" className="input-moj" onChange={handleFile}/>
          {form.poster_url && <img src={form.poster_url} alt="Preview" className="poster-43 mt-3 max-w-xs"/>}</div>
        <div className="sm:col-span-2"><label className="label-moj">Poster Alt Text</label>
          <input className="input-moj" value={form.poster_alt_text} onChange={(e) => setForm({ ...form, poster_alt_text: e.target.value })} placeholder="Describe the poster for accessibility"/></div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.rsvp_enabled} onChange={(e) => setForm({ ...form, rsvp_enabled: e.target.checked })}/> Enable RSVP
        </label>
      </div>
      <button disabled={isSubmitting} className="btn-primary" data-testid="event-submit">
        {editing ? 'Update Event' : 'Create Event'}
      </button>
    </form>
  );
}

function DeletePermanentlyButton({ event, onConfirm, disabled }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          disabled={disabled}
          data-testid={`event-delete-permanent-btn-${event.slug}`}
          className="text-red-700 hover:bg-red-100 p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Delete permanently"
          title="Delete permanently (removes RSVPs and registrations)"
        >
          <Trash2 size={16} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="event-delete-permanent-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete this event?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove{' '}
            <span className="font-semibold text-moj-purple-deep">
              &ldquo;{event.title}&rdquo;
            </span>{' '}
            along with every RSVP and VBS registration linked to it. This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="event-delete-permanent-cancel-btn">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(event.id)}
            data-testid="event-delete-permanent-confirm-btn"
            className="bg-red-600 text-white hover:bg-red-700"
          >
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EventRow({ event, onEdit, onArchive, onPermanentDelete, isDeleting }) {
  return (
    <div className="card-moj p-4 flex flex-wrap items-center gap-4" data-testid={`event-row-${event.slug}`}>
      {event.poster_url && <img src={event.poster_url} alt={event.poster_alt_text || event.title} className="w-20 h-15 object-cover rounded"/>}
      <div className="flex-1">
        <div className="font-semibold text-moj-purple-deep">{event.title}</div>
        <div className="text-xs text-moj-charcoal/60">{event.event_date} @ {event.event_time} • {event.location}</div>
      </div>
      <StatusPill status={event.status}/>
      <RSVPViewer eventId={event.id} eventTitle={event.title}/>
      <button onClick={() => onEdit(event)} className="text-moj-purple hover:bg-moj-cream p-2 rounded" aria-label="Edit" data-testid={`event-edit-btn-${event.slug}`}><Edit size={16}/></button>
      {event.status !== 'Archived' && (
        <button onClick={() => onArchive(event.id)} className="text-red-600 hover:bg-red-50 p-2 rounded" aria-label="Archive" data-testid={`event-archive-btn-${event.slug}`}><Archive size={16}/></button>
      )}
      {event.status === 'Archived' && (
        <DeletePermanentlyButton
          event={event}
          onConfirm={onPermanentDelete}
          disabled={isDeleting}
        />
      )}
    </div>
  );
}

function useEventMutations(qc, resetForm) {
  const create = useMutation({
    mutationFn: async (p) => (await api.post('/admin/events', p)).data,
    onSuccess: () => { toast.success('Event created'); qc.invalidateQueries({ queryKey: ['adm-events'] }); resetForm(); },
  });
  const update = useMutation({
    mutationFn: async ({ id, ...p }) => (await api.put(`/admin/events/${id}`, p)).data,
    onSuccess: () => { toast.success('Event updated'); qc.invalidateQueries({ queryKey: ['adm-events'] }); resetForm(); },
  });
  const archive = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/events/${id}`)).data,
    onSuccess: () => { toast.success('Event archived'); qc.invalidateQueries({ queryKey: ['adm-events'] }); },
  });
  const permanentDelete = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/events/${id}/permanent`)).data,
    onSuccess: (data) => {
      const rsvps = data?.rsvps_deleted ?? 0;
      const regs = data?.registrations_deleted ?? 0;
      toast.success(
        `Event deleted permanently (${rsvps} RSVP${rsvps === 1 ? '' : 's'}, ${regs} registration${regs === 1 ? '' : 's'} removed).`
      );
      qc.invalidateQueries({ queryKey: ['adm-events'] });
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || 'Failed to delete event permanently.';
      toast.error(msg);
    },
  });
  return { create, update, archive, permanentDelete };
}

export default function EventsTab() {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({ queryKey: ['adm-events'], queryFn: async () => (await api.get('/admin/events')).data });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);

  const resetForm = () => { setEditing(null); setShowForm(false); setForm(EMPTY_EVENT); };
  const { create, update, archive, permanentDelete } = useEventMutations(qc, resetForm);

  const onSubmit = (e) => {
    e.preventDefault();
    if (editing) update.mutate({ id: editing, ...form });
    else create.mutate(form);
  };

  const openEdit = (ev) => { setEditing(ev.id); setForm({ ...EMPTY_EVENT, ...ev }); setShowForm(true); };
  const toggleForm = () => { setEditing(null); setForm(EMPTY_EVENT); setShowForm((s) => !s); };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-serif text-lg text-moj-purple-deep">Events ({events.length})</h3>
        <button onClick={toggleForm} className="btn-gold text-sm py-2 px-4" data-testid="new-event-btn">
          <Plus size={14}/> {showForm ? 'Close' : 'New Event'}
        </button>
      </div>

      {showForm && (
        <EventForm form={form} setForm={setForm} editing={editing}
          onSubmit={onSubmit} isSubmitting={create.isPending || update.isPending}/>
      )}

      <div className="space-y-2">
        {events.map((ev) => (
          <EventRow
            key={ev.id}
            event={ev}
            onEdit={openEdit}
            onArchive={archive.mutate}
            onPermanentDelete={permanentDelete.mutate}
            isDeleting={permanentDelete.isPending}
          />
        ))}
      </div>
    </div>
  );
}
